import React, { useState } from 'react';
import { Typography, Table, Icon, Spinner, TextField, SelectMenu, Button } from '@scale/scaleui';
import { ResumeMetadataFields } from '@scale/shared-types';
import { useGetResumeMetadataValuesQuery } from 'react-scale/components/GrowthCenter/ResumeMetadata/data/useGetResumeMetadataValues';
import Pagination from 'react-scale/components/design-system/Pagination';
import { METADATA_VALUES_DISPLAY_PAGE_SIZE } from 'react-scale/components/GrowthCenter/ResumeSearch/constants';
import NormalizeModal from './NormalizeModal';

const RESUME_METADATA_TABLE_COLUMNS = [
  {
    header: 'Value',
    id: 'value',
  },
  {
    header: 'Number of Resumes',
    id: 'count',
  },
  {
    header: 'Actions',
    id: 'actions',
  },
];

const ResumeMetadataPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedField, setSelectedField] = useState<ResumeMetadataFields>(
    ResumeMetadataFields.School,
  );
  const [page, setPage] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data, isLoading } = useGetResumeMetadataValuesQuery({
    field: selectedField,
    search: searchTerm,
    page,
    pageSize: METADATA_VALUES_DISPLAY_PAGE_SIZE,
  });

  const formattedResumeMetadataValues: { value: string; count: number; actions: JSX.Element }[] =
    (data?.values || []).map(value => ({
      value: value.value,
      count: value.count,
      actions: (
        <div className="flex flex-row items-center">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              navigator.clipboard.writeText(value.value);
            }}
          >
            Copy
          </Button>
        </div>
      ),
    })) ?? [];

  const handlePageChange = (i: number) => {
    const pageNumber = i - 1; // i is 1-indexed
    setPage(pageNumber);
  };

  const totalPages = data?.totalPages ?? 0;
  const totalTerms = data?.totalTerms ?? 0;

  return (
    <div className="flex flex-col h-full w-full gap-2">
      <div className="flex flex-row justify-between items-center">
        <Typography.Paragraph1 className="text-neutral-900 font-medium">
          View existing Resume Metadata values and the number of associated resumes
        </Typography.Paragraph1>
        <Button variant="primary" onClick={() => setIsModalOpen(true)}>
          Normalize CSV of terms
        </Button>
      </div>
      <div className="flex flex-row gap-2 items-center">
        <Typography.Paragraph2>Select Field</Typography.Paragraph2>
        <SelectMenu
          options={[
            ...Object.values(ResumeMetadataFields).map(value => ({ id: value, name: value })),
          ]}
          selectedOptionId={selectedField}
          setSelected={value => {
            setSelectedField(value as ResumeMetadataFields);
            setSearchTerm('');
            setPage(0);
          }}
        />
      </div>
      <div className="flex flex-row gap-2 items-center mt-1">
        <TextField
          className="w-[35%]"
          leadingIcon="search"
          id="search"
          value={searchTerm}
          onChange={(e: any) => {
            setSearchTerm(e.target.value);
            setPage(0);
          }}
          placeholder="Search values..."
        />
      </div>
      <div className="flex flex-row mt-2 w-full h-full">
        {isLoading ? (
          <div className="flex flex-grow h-full w-full justify-center items-center">
            <Spinner size="md" />
          </div>
        ) : (
          <>
            {!searchTerm ? (
              <div className="flex flex-col gap-2 h-full w-full justify-center items-center">
                <Icon icon={['fas', 'search']} size="lg" className="text-neutral-500" />
                <Typography.Paragraph2 className="font-medium text-neutral-700">
                  Enter a search term to start viewing metadata values
                </Typography.Paragraph2>
              </div>
            ) : formattedResumeMetadataValues.length > 0 && totalTerms > 0 ? (
              <div className="flex flex-col w-full pb-4">
                <Typography.Paragraph1>
                  Results ({totalTerms.toLocaleString()})
                </Typography.Paragraph1>
                <Table
                  columns={RESUME_METADATA_TABLE_COLUMNS}
                  data={formattedResumeMetadataValues}
                  defaultPageSize={METADATA_VALUES_DISPLAY_PAGE_SIZE}
                  defaultRowHeight={40}
                  manualPagination
                  isLoading={isLoading}
                />
                {totalPages > 0 && (
                  <Pagination
                    size="large"
                    className="w-full mt-4"
                    count={totalPages}
                    page={page + 1}
                    onChange={handlePageChange}
                  />
                )}
              </div>
            ) : (
              <div className="flex flex-grow h-full w-full justify-center items-center">
                <Typography.Paragraph2>No results found</Typography.Paragraph2>
              </div>
            )}
          </>
        )}
      </div>

      <NormalizeModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default ResumeMetadataPage;
